
import { Exam } from './types';
import { printHTML, openInExternalBrowser } from '../printingBridge';
import { isDesktopEnvironment } from '../../desktop/desktopEnvironment';

export const generateExamReport = (
  exam: Exam, 
  student: { id: string; name: string; }, 
  logoUrl: string, 
  options?: { delay?: number; useExternalBrowser?: boolean }
): void => {
  // Generate exam certificate HTML
  const certificateHtml = generateExamCertificateHtml(exam, student, logoUrl);
  
  // Detect desktop environment
  const isDesktop = isDesktopEnvironment();
  const forceExternalBrowser = options?.useExternalBrowser || isDesktop;
  
  if (forceExternalBrowser) {
    // For desktop apps (including Nativefier), use external browser
    openInExternalBrowser(certificateHtml, `${student.name} - ${exam.subject} Certificate`);
  } else {
    // For web environments, use the normal print function
    printHTML(certificateHtml, `${student.name} - ${exam.subject} Certificate`, {
      delay: options?.delay || 800
    });
  }
};

const generateExamCertificateHtml = (exam: Exam, student: { id: string; name: string; }, logoUrl: string): string => {
  // Calculate grade and status
  const percentage = (exam.marksObtained / exam.totalMarks) * 100;
  const grade = calculateGrade(percentage);
  const status = percentage >= 33 ? 'PASS' : 'FAIL';

  // Format date
  const examDate = new Date(exam.date);
  const formattedDate = `${examDate.getDate()}/${examDate.getMonth() + 1}/${examDate.getFullYear()}`;
  
  // Certificate border style
  const borderStyle = status === 'PASS' 
    ? 'border: 8px double #4caf50;' 
    : 'border: 8px double #ff5252;';

  return `
    <div style="max-width: 800px; margin: 0 auto; ${borderStyle} padding: 20px; position: relative;">
      <div style="text-align: center; margin-bottom: 20px;">
        <img src="${logoUrl}" alt="NALC Logo" style="max-width: 100px; height: auto;">
        <h1 style="margin: 10px 0; color: #333; font-size: 24px;">New Asian Learning Centre</h1>
        <h2 style="margin: 5px 0; color: #555; font-size: 18px;">Examination Certificate</h2>
      </div>
      
      <div style="text-align: center; margin-bottom: 30px;">
        <h3 style="font-size: 22px; color: #333; margin: 10px 0;">Certificate of ${exam.examType === 'final' ? 'Final Assessment' : 'Progress'}</h3>
      </div>
      
      <div style="margin-bottom: 30px;">
        <p style="font-size: 16px; line-height: 1.6;">
          This is to certify that <strong style="font-size: 18px;">${student.name}</strong> 
          (Student ID: ${student.id}) has appeared for the examination in 
          <strong>${exam.subject}</strong> conducted on <strong>${formattedDate}</strong> 
          and has achieved the following result:
        </p>
      </div>
      
      <div style="margin: 20px 0; border: 1px solid #ddd; padding: 15px;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px; font-weight: bold;">Subject</td>
            <td style="padding: 10px;">${exam.subject}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px; font-weight: bold;">Total Marks</td>
            <td style="padding: 10px;">${exam.totalMarks}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px; font-weight: bold;">Marks Obtained</td>
            <td style="padding: 10px;">${exam.marksObtained}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px; font-weight: bold;">Percentage</td>
            <td style="padding: 10px;">${percentage.toFixed(2)}%</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px; font-weight: bold;">Grade</td>
            <td style="padding: 10px;">${grade}</td>
          </tr>
          <tr>
            <td style="padding: 10px; font-weight: bold;">Status</td>
            <td style="padding: 10px; font-weight: bold; color: ${status === 'PASS' ? '#4caf50' : '#ff5252'};">
              ${status}
            </td>
          </tr>
        </table>
      </div>
      
      ${exam.remarks ? `
        <div style="margin: 20px 0;">
          <h4 style="color: #555;">Remarks:</h4>
          <p style="background-color: #f9f9f9; padding: 10px; border-left: 4px solid #ccc;">${exam.remarks}</p>
        </div>
      ` : ''}
      
      <div style="display: flex; justify-content: space-between; margin-top: 40px;">
        <div style="text-align: center; width: 45%;">
          <div style="border-top: 1px solid #333; padding-top: 5px; margin-top: 30px;">
            Examiner Signature
          </div>
        </div>
        <div style="text-align: center; width: 45%;">
          <div style="border-top: 1px solid #333; padding-top: 5px; margin-top: 30px;">
            Director Signature
          </div>
        </div>
      </div>
      
      <div style="margin-top: 40px; text-align: center; font-size: 14px; color: #777;">
        <p>New Asian Learning Centre - Excellence in Education</p>
        <p>Certificate issued on: ${new Date().toLocaleDateString()}</p>
      </div>
      
      <div style="position: absolute; top: 20px; right: 20px; opacity: 0.1; font-size: 120px; transform: rotate(45deg);">
        ${status}
      </div>
    </div>
  `;
};

const calculateGrade = (percentage: number): string => {
  if (percentage >= 90) return 'A+';
  if (percentage >= 80) return 'A';
  if (percentage >= 70) return 'B+';
  if (percentage >= 60) return 'B';
  if (percentage >= 50) return 'C+';
  if (percentage >= 40) return 'C';
  if (percentage >= 33) return 'D';
  return 'F';
};
